import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { MusicProvider } from "./context/MusicContext";
import { GlobalMusicBar } from "./components/GlobalMusicBar";
import { Toaster } from "./components/ui/sonner";

// Pages
import { LoginPage, RegisterPage } from "./pages/AuthPages";
import { DashboardPage } from "./pages/DashboardPage";
import { GamesPage } from "./pages/GamesPage";
import { ClientsPage } from "./pages/ClientsPage";
import { RefereesPage } from "./pages/RefereesPage";
import { InvoicesPage } from "./pages/InvoicesPage";
import { RefereePortalPage, ClientPortalPage } from "./pages/PortalPages";
import { PaymentSuccessPage } from "./pages/PaymentPages";
import { CommunityPage } from "./pages/CommunityPage";
import { LiveStreamPage } from "./pages/LiveStreamPage";
import { RefCampPage } from "./pages/RefCampPage";
import { GameBoardPage } from "./pages/GameBoardPage";
import { SubscriptionPage } from "./pages/SubscriptionPage";
import { MessagesPage } from "./pages/MessagesPage";
import { HomePage } from "./pages/HomePage";
import { AdminPlaylistPage } from "./pages/AdminPlaylistPage";
import { AdminInquiriesPage } from "./pages/AdminInquiriesPage";
import { AdminHomepagePage } from "./pages/AdminHomepagePage";
import { AdminTeamPage } from "./pages/AdminTeamPage";
import { AdminEmailSettingsPage } from "./pages/AdminEmailSettingsPage";
import { TeamPage } from "./pages/TeamPage";

// Layouts
import { DashboardLayout, PortalLayout } from "./components/Layout";

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-white border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    if (user.role === 'referee') {
      return <Navigate to="/referee-portal" replace />;
    } else if (user.role === 'client') {
      return <Navigate to="/client-portal" replace />;
    } else if (user.role === 'fan') {
      return <Navigate to="/" replace />;
    } else {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-white border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (user) {
    if (user.role === 'referee') {
      return <Navigate to="/referee-portal" replace />;
    } else if (user.role === 'client') {
      return <Navigate to="/client-portal" replace />;
    } else if (user.role === 'fan') {
      return <Navigate to="/" replace />;
    } else {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return children;
};

// Wrapper components for routes that need different layouts based on role
const GameBoardWrapper = () => {
  const { user } = useAuth();
  return user?.role === 'referee' ? (
    <PortalLayout><GameBoardPage /></PortalLayout>
  ) : (
    <DashboardLayout><GameBoardPage /></DashboardLayout>
  );
};

const CommunityWrapper = () => {
  const { user } = useAuth();
  return user?.role === 'referee' ? (
    <PortalLayout><CommunityPage /></PortalLayout>
  ) : (
    <DashboardLayout><CommunityPage /></DashboardLayout>
  );
};

const MessagesWrapper = () => {
  const { user } = useAuth();
  return user?.role === 'referee' ? (
    <PortalLayout><MessagesPage /></PortalLayout>
  ) : (
    <DashboardLayout><MessagesPage /></DashboardLayout>
  );
};

const RefCampWrapper = () => {
  const { user } = useAuth();
  return user?.role === 'referee' ? (
    <PortalLayout><RefCampPage /></PortalLayout>
  ) : (
    <DashboardLayout><RefCampPage /></DashboardLayout>
  );
};

const SubscriptionWrapper = () => {
  const { user } = useAuth();
  return user?.role === 'client' ? (
    <PortalLayout><SubscriptionPage /></PortalLayout>
  ) : (
    <DashboardLayout><SubscriptionPage /></DashboardLayout>
  );
};

const GoogleAuthCallback = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { handleGoogleCallback } = useAuth();
  const processed = React.useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const hash = location.hash || '';
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) {
      navigate('/login', { replace: true });
      return;
    }
    const sessionId = match[1];
    const pendingRole = sessionStorage.getItem('oauth_role') || null;
    sessionStorage.removeItem('oauth_role');

    handleGoogleCallback(sessionId, pendingRole)
      .then((user) => {
        if (user.role === 'referee') navigate('/referee-portal', { replace: true });
        else if (user.role === 'client') navigate('/client-portal', { replace: true });
        else if (user.role === 'fan') navigate('/', { replace: true });
        else navigate('/dashboard', { replace: true });
      })
      .catch((err) => {
        console.error('Google auth failed', err);
        navigate('/login?error=google', { replace: true });
      });
  }, [location, navigate, handleGoogleCallback]);

  return (
    <div className="min-h-screen bg-black flex items-center justify-center text-white">
      <div className="text-center">
        <div className="animate-spin w-10 h-10 border-2 border-amber-500 border-t-transparent rounded-full mx-auto mb-4"></div>
        <p className="text-zinc-400">Signing you in with Google…</p>
      </div>
    </div>
  );
};

function AppRoutes() {
  const location = useLocation();
  // Detect OAuth callback synchronously — MUST happen before other protected routes
  if (location.hash && location.hash.includes('session_id=')) {
    return <GoogleAuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<HomePage />} />
      <Route path="/team" element={<TeamPage />} />
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />

      {/* Admin/Assignor Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute allowedRoles={['admin', 'assignor']}>
          <DashboardLayout><DashboardPage /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/games" element={
        <ProtectedRoute allowedRoles={['admin', 'assignor']}>
          <DashboardLayout><GamesPage /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/game-board" element={
        <ProtectedRoute allowedRoles={['admin', 'assignor', 'referee']}>
          <GameBoardWrapper />
        </ProtectedRoute>
      } />
      <Route path="/invoices" element={
        <ProtectedRoute allowedRoles={['admin', 'assignor']}>
          <DashboardLayout><InvoicesPage /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/referees" element={
        <ProtectedRoute allowedRoles={['admin', 'assignor']}>
          <DashboardLayout><RefereesPage /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/clients" element={
        <ProtectedRoute allowedRoles={['admin', 'assignor']}>
          <DashboardLayout><ClientsPage /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/community" element={
        <ProtectedRoute allowedRoles={['admin', 'assignor', 'referee']}>
          <CommunityWrapper />
        </ProtectedRoute>
      } />
      <Route path="/livestream" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <DashboardLayout><LiveStreamPage /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/messages" element={
        <ProtectedRoute allowedRoles={['admin', 'referee']}>
          <MessagesWrapper />
        </ProtectedRoute>
      } />
      <Route path="/ref-camp" element={
        <ProtectedRoute allowedRoles={['admin', 'assignor', 'referee']}>
          <RefCampWrapper />
        </ProtectedRoute>
      } />
      <Route path="/subscriptions" element={
        <ProtectedRoute allowedRoles={['admin', 'client']}>
          <SubscriptionWrapper />
        </ProtectedRoute>
      } />

      {/* Referee Portal */}
      <Route path="/referee-portal" element={
        <ProtectedRoute allowedRoles={['referee']}>
          <PortalLayout><RefereePortalPage /></PortalLayout>
        </ProtectedRoute>
      } />

      {/* Client Portal */}
      <Route path="/client-portal" element={
        <ProtectedRoute allowedRoles={['client']}>
          <PortalLayout><ClientPortalPage /></PortalLayout>
        </ProtectedRoute>
      } />

      {/* Payment Success */}
      <Route path="/payment/success" element={
        <ProtectedRoute>
          <PaymentSuccessPage />
        </ProtectedRoute>
      } />
      <Route path="/subscription/success" element={
        <ProtectedRoute>
          <PaymentSuccessPage />
        </ProtectedRoute>
      } />

      {/* Admin Playlist & Inquiries */}
      <Route path="/admin/playlist" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <DashboardLayout><AdminPlaylistPage /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/homepage" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <DashboardLayout><AdminHomepagePage /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/team" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <DashboardLayout><AdminTeamPage /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/inquiries" element={
        <ProtectedRoute allowedRoles={['admin', 'assignor']}>
          <DashboardLayout><AdminInquiriesPage /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/email" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <DashboardLayout><AdminEmailSettingsPage /></DashboardLayout>
        </ProtectedRoute>
      } />

      {/* Default Redirect */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <MusicProvider>
            <AppRoutes />
            <GlobalMusicBar />
            <Toaster position="top-right" richColors />
          </MusicProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
